import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';

/**
 * Multi-select dropdown — used by the user role editor on UsersAdmin
 * and reusable for any future tag-style picker. Selected values render
 * as inline chips inside the trigger; the panel is a list of toggleable
 * rows. Same look-and-feel as the nav Dropdown so the app feels coherent.
 */

interface OptionGroup<T extends string> {
  title?: string;
  options: { value: T; label: string; description?: string }[];
}

interface MultiSelectProps<T extends string> {
  groups: OptionGroup<T>[];
  value: T[];
  onChange: (next: T[]) => void;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  /** Maximum chips rendered inside the trigger before collapsing to "+N". */
  maxChips?: number;
  /**
   * Minimum width (px) for the dropdown menu, regardless of trigger size.
   * Lets the menu breathe when the trigger lives in a tight table cell.
   * The menu still won't exceed the viewport width.
   */
  menuMinWidth?: number;
}

export function MultiSelect<T extends string>({
  groups,
  value,
  onChange,
  placeholder = 'Select...',
  ariaLabel,
  className = '',
  maxChips = 4,
  menuMinWidth = 280,
}: MultiSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  // Viewport-anchored position for the portaled menu. Recomputed when
  // the menu opens and on scroll / resize so it tracks the trigger.
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
    placement: 'below' | 'above';
  } | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    function reposition() {
      const t = triggerRef.current;
      if (!t) return;
      const rect = t.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Menu has max-height 320px (max-h-80) + a little gap. If there's
      // not enough room below AND there's more above, flip up.
      const desired = 320 + 8;
      const placement: 'below' | 'above' =
        spaceBelow < desired && spaceAbove > spaceBelow ? 'above' : 'below';

      // Menu width: at least menuMinWidth, never wider than the trigger
      // when the trigger is already big enough, and never wider than the
      // viewport minus a small margin. When the menu is wider than the
      // trigger, anchor it to the trigger's right edge if that keeps it
      // on-screen, otherwise to the left edge.
      const viewportMargin = 8;
      const maxWidth = window.innerWidth - viewportMargin * 2;
      const width = Math.min(Math.max(rect.width, menuMinWidth), maxWidth);
      let left = rect.left;
      if (width > rect.width) {
        // Prefer right-aligning to the trigger so the menu opens inward.
        const rightAligned = rect.right - width;
        if (rightAligned >= viewportMargin) left = rightAligned;
        // Then clamp to viewport.
        left = Math.min(
          Math.max(left, viewportMargin),
          window.innerWidth - width - viewportMargin,
        );
      }

      setPosition({
        top: placement === 'below' ? rect.bottom + 4 : rect.top - 4,
        left,
        width,
        placement,
      });
    }
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      // The menu lives in a portal so it isn't inside rootRef — check
      // both the trigger container and the menu before closing.
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // build label lookup so chips can render the human label, not the raw value
  const labelByValue = new Map<T, string>();
  for (const g of groups) {
    for (const o of g.options) labelByValue.set(o.value, o.label);
  }

  function toggle(v: T) {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  }

  function removeChip(v: T, e: React.MouseEvent) {
    e.stopPropagation();
    onChange(value.filter((x) => x !== v));
  }

  const shown = value.slice(0, maxChips);
  const overflow = value.length - shown.length;

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        className={[
          'flex w-full min-h-9 items-center gap-1.5 rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1.5 text-left text-xs shadow-token-sm',
          'hover:bg-surface-2 transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-strong focus-visible:ring-offset-1',
        ].join(' ')}
      >
        {value.length === 0 ? (
          <span className="text-fg-dim">{placeholder}</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {shown.map((v) => (
              <span
                key={v}
                className="inline-flex items-center gap-1 rounded-token-sm bg-accent/10 px-1.5 py-0.5 text-[11px] font-medium text-accent"
              >
                {labelByValue.get(v) ?? v}
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={`Remove ${labelByValue.get(v) ?? v}`}
                  onClick={(e) => removeChip(v, e)}
                  className="rounded p-0.5 text-accent/70 hover:bg-accent/15 hover:text-accent"
                >
                  <X className="h-2.5 w-2.5" aria-hidden />
                </span>
              </span>
            ))}
            {overflow > 0 ? (
              <span className="inline-flex items-center rounded-token-sm bg-neutral-soft/15 px-1.5 py-0.5 text-[11px] font-medium text-fg-muted">
                +{overflow} more
              </span>
            ) : null}
          </span>
        )}
        <ChevronDown
          className={`ml-auto h-4 w-4 shrink-0 text-fg-muted transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {open && position
        ? createPortal(
            <div
              ref={menuRef}
              id={menuId}
              role="listbox"
              aria-multiselectable
              style={{
                position: 'fixed',
                top:
                  position.placement === 'below' ? position.top : undefined,
                bottom:
                  position.placement === 'above'
                    ? window.innerHeight - position.top
                    : undefined,
                left: position.left,
                width: position.width,
              }}
              className="aurora-fade-in z-[1000] max-h-80 overflow-auto rounded-token border border-line/10 bg-surface p-1 shadow-token-lg"
            >
              {groups.map((g, gi) => (
                <div key={gi} className="py-1 first:pt-0 last:pb-0">
                  {g.title ? (
                    <div className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-fg-dim">
                      {g.title}
                    </div>
                  ) : null}
                  {g.options.map((o) => {
                    const selected = value.includes(o.value);
                    return (
                      <button
                        key={o.value}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => toggle(o.value)}
                        className={[
                          'flex w-full items-start gap-2 rounded-token-sm px-2 py-1.5 text-left text-xs transition-colors',
                          'min-h-touch sm:min-h-0',
                          selected
                            ? 'bg-accent/10 text-fg'
                            : 'text-fg-muted hover:bg-surface-2',
                        ].join(' ')}
                      >
                        <span
                          aria-hidden
                          className={[
                            'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                            selected
                              ? 'border-accent bg-accent text-fg-on-accent'
                              : 'border-line-strong/30 bg-surface',
                          ].join(' ')}
                        >
                          {selected ? <Check className="h-3 w-3" /> : null}
                        </span>
                        <span className="min-w-0">
                          <span className="block font-medium leading-tight">
                            {o.label}
                          </span>
                          {o.description ? (
                            <span className="mt-0.5 block text-[11px] text-fg-dim">
                              {o.description}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
